import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import usePrefersReducedMotion from "@/hooks/usePrefersReducedMotion";

export const HOME_LOADER_SESSION_KEY = "byron-home-loader-seen";

/** Safe sessionStorage read — treat storage errors as "already seen" so we never block. */
export function hasSeenHomeLoader() {
  try {
    return sessionStorage.getItem(HOME_LOADER_SESSION_KEY) === "1";
  } catch {
    return true;
  }
}

export function markHomeLoaderSeen() {
  try {
    sessionStorage.setItem(HOME_LOADER_SESSION_KEY, "1");
  } catch {
    // ignore quota / private-mode failures
  }
}

const LOGO_SRC = "/byron-logo-transparent-26.svg";
const MIN_VISIBLE_MS = 900;
const MAX_WAIT_MS = 1400;
const REDUCED_MIN_MS = 280;

/**
 * Full-screen Byron wordmark loader shown once per browser session on homepage entry.
 * Waits for critical hero readiness with a bounded timeout — never blocks indefinitely.
 */
const LogoLoader = ({ heroReady = false, onComplete }) => {
  const reducedMotion = usePrefersReducedMotion();
  const [visible, setVisible] = useState(true);
  const startedAt = useRef(
    typeof performance !== "undefined" ? performance.now() : Date.now()
  );
  const finishedRef = useRef(false);
  const exitScheduled = useRef(false);
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;

  const unlockScroll = () => {
    if (typeof document === "undefined") return;
    document.documentElement.style.overflow = "";
    document.body.style.overflow = "";
  };

  const lockScroll = () => {
    if (typeof document === "undefined") return;
    document.documentElement.style.overflow = "hidden";
    document.body.style.overflow = "hidden";
  };

  const finish = () => {
    if (finishedRef.current) return;
    finishedRef.current = true;
    markHomeLoaderSeen();
    unlockScroll();
    onCompleteRef.current?.();
  };

  // Lock scroll while visible; always release on unmount / all exits
  useEffect(() => {
    lockScroll();
    return () => {
      unlockScroll();
    };
  }, []);

  // Begin exit when hero is ready (or max wait), after minimum visible time
  useEffect(() => {
    if (!visible || exitScheduled.current) return undefined;

    const minDelay = reducedMotion ? REDUCED_MIN_MS : MIN_VISIBLE_MS;
    let exitTimer;
    let maxTimer;

    const scheduleExit = () => {
      if (exitScheduled.current) return;
      exitScheduled.current = true;
      const now =
        typeof performance !== "undefined" ? performance.now() : Date.now();
      const elapsed = now - startedAt.current;
      const remaining = Math.max(0, minDelay - elapsed);
      exitTimer = window.setTimeout(() => setVisible(false), remaining);
    };

    if (heroReady) {
      scheduleExit();
    } else {
      maxTimer = window.setTimeout(scheduleExit, MAX_WAIT_MS);
    }

    return () => {
      if (exitTimer) window.clearTimeout(exitTimer);
      if (maxTimer) window.clearTimeout(maxTimer);
    };
  }, [heroReady, visible, reducedMotion]);

  return (
    <AnimatePresence onExitComplete={finish}>
      {visible && (
        <motion.div
          key="byron-logo-loader"
          role="status"
          aria-live="polite"
          aria-busy="true"
          aria-label="Byrōn Properties"
          data-testid="logo-loader"
          className="fixed inset-0 z-[100] flex items-center justify-center"
          style={{ backgroundColor: "#F3EEE7" }}
          initial={{ opacity: 1 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{
            duration: reducedMotion ? 0.2 : 0.5,
            ease: [0.22, 1, 0.36, 1],
          }}
        >
          <span className="sr-only">Loading Byrōn Properties</span>
          <div className="relative overflow-hidden px-8">
            <motion.img
              src={LOGO_SRC}
              alt="Byrōn"
              width={220}
              height={46}
              draggable={false}
              className="w-[min(58vw,220px)] h-auto select-none"
              data-testid="logo-loader-mark"
              initial={
                reducedMotion
                  ? { opacity: 1 }
                  : { opacity: 0, clipPath: "inset(0 42% 0 42%)" }
              }
              animate={
                reducedMotion
                  ? { opacity: 1 }
                  : { opacity: 1, clipPath: "inset(0 0% 0 0%)" }
              }
              transition={
                reducedMotion
                  ? { duration: 0 }
                  : { duration: 0.95, ease: [0.22, 1, 0.36, 1] }
              }
            />
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default LogoLoader;
